import './CheckoutPage.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import { useDeliveryAddress } from '../../address/DeliveryAddressContext';
import GuestLoginPanel from '../GuestAuthDropdown/GuestLoginPanel';
import { useGuestCart } from '../../guestCart/hooks/useGuestCart';
import { useGuestCartStore } from '../../guestCart/store/useGuestCartStore';
import { buildWebOrderPayload } from '../../guestCart/utils/mapCartToOrderPayload';
import { createWebOrder } from '../../services/api/orderService';
import { createAddress } from '../../services/api/addressService';
import OrderSummaryAside from './OrderSummaryAside';
import SavedAddressPicker from './SavedAddressPicker';
import locationIcon from '../RestaurantDetailPage/img/location.png';

/** Checkout is delivery-only (no order-type toggle on this page). */
const ORDER_TYPE = 'delivery';

const DELIVERY_SPEED = {
  saver: { label: 'Saver', eta: '40–55 min', fee: 0.29 },
  standard: { label: 'Standard', eta: '25–35 min', fee: 0 },
  priority: { label: 'Priority', eta: '15–20 min', fee: 1.49 },
};

const FALLBACK_MAP_CENTER = { latitude: 31.5204, longitude: 74.3587 };

const joinUniqueParts = (parts) => {
  const seen = new Set();
  return parts.filter((part) => {
    const normalized = String(part || '').trim();
    if (!normalized) return false;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getGoogleAddressParts = (result) => {
  const parts = {
    streetNumber: '',
    route: '',
    premise: '',
    subpremise: '',
    suburb: '',
    city: '',
    county: '',
    state: '',
    postcode: '',
    country: '',
  };

  (result?.address_components || []).forEach((component) => {
    const types = component.types || [];
    if (types.includes('street_number')) parts.streetNumber = component.long_name;
    if (types.includes('route')) parts.route = component.long_name;
    if (types.includes('premise')) parts.premise = component.long_name;
    if (types.includes('subpremise')) parts.subpremise = component.long_name;
    if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) {
      parts.suburb = component.long_name;
    }
    if (types.includes('locality') || types.includes('postal_town')) parts.city = component.long_name;
    if (types.includes('administrative_area_level_2')) parts.county = component.long_name;
    if (types.includes('administrative_area_level_1')) parts.state = component.long_name;
    if (types.includes('postal_code')) parts.postcode = component.long_name;
    if (types.includes('country')) parts.country = component.long_name;
  });

  return parts;
};

const formatGoogleResult = (result) => {
  const parts = getGoogleAddressParts(result);
  const streetLine = joinUniqueParts([
    parts.premise,
    [parts.streetNumber, parts.route].filter(Boolean).join(' ').trim(),
    parts.subpremise,
  ]).join(', ');
  const localityLine = joinUniqueParts([parts.suburb, parts.city]).join(', ');
  const regionLine = joinUniqueParts([parts.county, parts.state, parts.postcode]).join(', ');

  return joinUniqueParts([streetLine, localityLine, regionLine, parts.country]).join(', ') || result?.formatted_address || '';
};

const pickBestGeocodeResult = (results = []) => {
  const preferredTypeOrder = ['street_address', 'premise', 'subpremise', 'route', 'establishment', 'plus_code'];

  for (const type of preferredTypeOrder) {
    const match = results.find((result) => result.types?.includes(type));
    if (match) return match;
  }

  return results[0] || null;
};

const getBestCurrentPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    let bestPosition = null;
    let resolved = false;
    let watchId = null;

    const cleanup = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      window.clearTimeout(forceResolveTimer);
    };

    const finish = (position) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(position);
    };

    const handleSuccess = (position) => {
      const accuracy = position?.coords?.accuracy ?? Number.POSITIVE_INFINITY;
      const bestAccuracy = bestPosition?.coords?.accuracy ?? Number.POSITIVE_INFINITY;

      if (!bestPosition || accuracy < bestAccuracy) bestPosition = position;
      if (accuracy <= 30) finish(position);
    };

    const handleError = (error) => {
      if (bestPosition) {
        finish(bestPosition);
        return;
      }

      if (!resolved) {
        resolved = true;
        cleanup();
        reject(error);
      }
    };

    const forceResolveTimer = window.setTimeout(() => {
      if (bestPosition) {
        finish(bestPosition);
      } else {
        handleError(new Error('Unable to get a precise location'));
      }
    }, 8000);

    watchId = navigator.geolocation.watchPosition(handleSuccess, handleError, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });
  });

const coordsToLatLngLiteral = (coords) => ({
  lat: Number(coords.latitude),
  lng: Number(coords.longitude),
});

const isValidCoords = (coords) => {
  const lat = Number(coords?.latitude);
  const lng = Number(coords?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

function buildOrderNotes({ leaveAtDoor, tipAmount, payment }) {
  const parts = [];
  if (leaveAtDoor) parts.push('Leave at door');
  if (tipAmount > 0) parts.push(`Rider tip: $${tipAmount.toFixed(2)}`);
  parts.push(`Payment: ${payment}`);
  return parts.join(' · ');
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { currentAddress: sharedAddress, setCurrentAddress: setSharedAddress } = useDeliveryAddress();
  const { restaurant, lines, totals, actions, getLinePricing, hasItems } = useGuestCart();
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const googleMapsPromiseRef = useRef(null);
  const hasRequestedLocationRef = useRef(false);
  const locationRequestIdRef = useRef(0);

  const [speed, setSpeed] = useState('standard');
  const [tip, setTip] = useState(0);
  const [payment, setPayment] = useState('card');
  const [leaveAtDoor, setLeaveAtDoor] = useState(true);
  const [address, setAddress] = useState(() => sharedAddress?.address || '');
  const [coords, setCoords] = useState(() => sharedAddress?.coords || null);
  const [locationStatus, setLocationStatus] = useState('Detecting your location...');
  const [locationError, setLocationError] = useState('');
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const savedAddressPickerRef = useRef(null);

  const addressSourceRef = useRef(sharedAddress?.source || 'manual');

  useEffect(() => {
    const trimmed = String(address || '').trim();
    if (!trimmed) return;
    setSharedAddress({
      address: trimmed,
      coords: coords && isValidCoords(coords) ? coords : null,
      source: addressSourceRef.current,
    });
  }, [address, coords, setSharedAddress]);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const deliveryFee = DELIVERY_SPEED[speed]?.fee ?? 0;

  const ensureGoogleMaps = useCallback(() => {
    if (window.google?.maps?.Map && window.google?.maps?.Geocoder) {
      return Promise.resolve(window.google.maps);
    }

    if (!GOOGLE_MAPS_API_KEY) {
      const err = new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY');
      setLocationError('Map is not configured. Please enter your address manually.');
      return Promise.reject(err);
    }

    if (!googleMapsPromiseRef.current) {
      googleMapsPromiseRef.current = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(window.google.maps), { once: true });
          existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.dataset.googleMapsLoader = 'true';
        script.onload = () => resolve(window.google.maps);
        script.onerror = () => reject(new Error('Failed to load Google Maps'));
        document.head.appendChild(script);
      }).catch((error) => {
        setLocationError('Failed to load map. Please check Google Maps API key and network.');
        throw error;
      });
    }

    return googleMapsPromiseRef.current;
  }, [GOOGLE_MAPS_API_KEY]);

  const geocodeRequest = useCallback(
    async (request) => {
      const maps = await ensureGoogleMaps();
      if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();

      return new Promise((resolve, reject) => {
        geocoderRef.current.geocode(request, (results, status) => {
          if (status === 'OK' && results?.length) {
            resolve(results);
            return;
          }

          reject(new Error(`Google geocode failed: ${status}`));
        });
      });
    },
    [ensureGoogleMaps]
  );

  const reverseGeocodeToAddress = useCallback(
    async (nextCoords) => {
      const results = await geocodeRequest({
        location: { lat: Number(nextCoords.latitude), lng: Number(nextCoords.longitude) },
      });
      const bestResult = pickBestGeocodeResult(results);
      return formatGoogleResult(bestResult) || `${Number(nextCoords.latitude).toFixed(6)}, ${Number(nextCoords.longitude).toFixed(6)}`;
    },
    [geocodeRequest]
  );

  const applyCoords = useCallback(
    async (nextCoords, { centerMap = true } = {}) => {
      if (!isValidCoords(nextCoords)) return;

      const requestId = locationRequestIdRef.current + 1;
      locationRequestIdRef.current = requestId;
      setIsResolvingLocation(true);
      setLocationError('');
      setCoords(nextCoords);
      setLocationStatus('Matching address with selected map location...');

      if (centerMap && mapRef.current) {
        mapRef.current.panTo(coordsToLatLngLiteral(nextCoords));
      }

      try {
        const resolvedAddress = await reverseGeocodeToAddress(nextCoords);
        if (locationRequestIdRef.current !== requestId) return;
        setAddress(resolvedAddress);
        setLocationStatus('Address matched with selected latitude and longitude.');
      } catch (error) {
        if (locationRequestIdRef.current !== requestId) return;
        setAddress(`${Number(nextCoords.latitude).toFixed(6)}, ${Number(nextCoords.longitude).toFixed(6)}`);
        setLocationStatus('Location selected. Please confirm the delivery address.');
        setLocationError('Could not find a full address for this pin.');
      } finally {
        if (locationRequestIdRef.current === requestId) setIsResolvingLocation(false);
      }
    },
    [reverseGeocodeToAddress]
  );

  const requestCurrentLocation = useCallback(async () => {
    setIsResolvingLocation(true);
    setLocationError('');
    setLocationStatus('Detecting your current location...');
    addressSourceRef.current = 'current';

    try {
      await ensureGoogleMaps();
      const position = await getBestCurrentPosition();
      const nextCoords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      await applyCoords(nextCoords);
    } catch (error) {
      setLocationError('Unable to detect your location. Click the map or drag the pin to choose it.');
      setLocationStatus('Choose your delivery location on the map.');
      setIsResolvingLocation(false);
    }
  }, [applyCoords, ensureGoogleMaps]);

  const matchTypedAddressToMap = useCallback(async () => {
    const typedAddress = String(address || '').trim();
    if (!typedAddress) return;

    setIsResolvingLocation(true);
    setLocationError('');
    setLocationStatus('Matching typed address to the map...');

    try {
      const results = await geocodeRequest({ address: typedAddress });
      const bestResult = pickBestGeocodeResult(results);
      const loc = bestResult?.geometry?.location;
      const latitude = typeof loc?.lat === 'function' ? loc.lat() : undefined;
      const longitude = typeof loc?.lng === 'function' ? loc.lng() : undefined;

      if (!isValidCoords({ latitude, longitude })) {
        throw new Error('No coordinates found for this address');
      }

      setAddress(formatGoogleResult(bestResult) || typedAddress);
      setCoords({ latitude, longitude });
      setLocationStatus('Address matched with selected latitude and longitude.');
      if (mapRef.current) mapRef.current.panTo({ lat: latitude, lng: longitude });
    } catch (error) {
      setLocationError('Could not match this address to the map. Please click or drag the pin.');
      setLocationStatus('Choose your delivery location on the map.');
    } finally {
      setIsResolvingLocation(false);
    }
  }, [address, geocodeRequest]);

  useEffect(() => {
    actions.setFee({ delivery: deliveryFee, taxRatePct: 10.25 });
  }, [actions, deliveryFee]);

  useEffect(
    () => () => {
      useGuestCartStore.getState().actions.setFee({ delivery: 0, taxRatePct: 0 });
    },
    []
  );

  useEffect(() => {
    if (!isAuthenticated || !mapElRef.current || mapRef.current) return;

    let cancelled = false;

    ensureGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapElRef.current) return;

        const center = coordsToLatLngLiteral(coords || FALLBACK_MAP_CENTER);
        mapRef.current = new maps.Map(mapElRef.current, {
          center,
          zoom: coords ? 16 : 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        markerRef.current = new maps.Marker({
          position: center,
          map: mapRef.current,
          draggable: true,
          title: 'Delivery location',
        });

        mapRef.current.addListener('click', (event) => {
          if (!event?.latLng) return;
          void applyCoords({ latitude: event.latLng.lat(), longitude: event.latLng.lng() }, { centerMap: false });
        });

        markerRef.current.addListener('dragend', (event) => {
          if (!event?.latLng) return;
          void applyCoords({ latitude: event.latLng.lat(), longitude: event.latLng.lng() }, { centerMap: false });
        });

        setMapReady(true);
      })
      .catch(() => {
        setMapReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyCoords, coords, ensureGoogleMaps, isAuthenticated]);

  useEffect(() => {
    if (!mapReady || !isValidCoords(coords) || !markerRef.current) return;

    const position = coordsToLatLngLiteral(coords);
    markerRef.current.setPosition(position);
    mapRef.current?.setZoom(16);
  }, [coords, mapReady]);

  useEffect(() => {
    if (!isAuthenticated || hasRequestedLocationRef.current) return;
    hasRequestedLocationRef.current = true;
    void requestCurrentLocation();
  }, [isAuthenticated, requestCurrentLocation]);

  const summary = useMemo(
    () => (
      <OrderSummaryAside
        restaurantName={restaurant?.name}
        lines={lines}
        getLinePricing={getLinePricing}
        totals={totals}
        tipAmount={tip}
        showTipRow
        orderType={ORDER_TYPE}
      />
    ),
    [restaurant?.name, lines, getLinePricing, totals, tip]
  );

  const saveCurrentAddress = useCallback(async () => {
    const trimmed = String(address || '').trim();
    if (!trimmed) {
      toast.error('Enter an address first');
      return;
    }
    setSavingAddress(true);
    try {
      await createAddress(
        {
          address_line: trimmed,
          latitude: isValidCoords(coords) ? coords.latitude : null,
          longitude: isValidCoords(coords) ? coords.longitude : null,
        },
        token,
      );
      toast.success('Address saved to your profile');
      savedAddressPickerRef.current?.();
    } catch (e) {
      toast.error(e?.message || 'Could not save address');
    } finally {
      setSavingAddress(false);
    }
  }, [address, coords, token]);

  const handleSavedAddressSelect = useCallback(
    ({ address: savedAddr, coords: savedCoords }) => {
      addressSourceRef.current = 'saved';
      setAddress(savedAddr || '');
      if (savedCoords && isValidCoords(savedCoords)) {
        setCoords(savedCoords);
        setLocationStatus('Address matched with selected latitude and longitude.');
        setLocationError('');
        if (mapRef.current) {
          mapRef.current.panTo(coordsToLatLngLiteral(savedCoords));
          mapRef.current.setZoom(16);
        }
      } else {
        setCoords(null);
        setLocationStatus('Address loaded. Match it to the map before placing the order.');
        if (savedAddr) void matchTypedAddressToMap();
      }
    },
    [matchTypedAddressToMap],
  );

  const placeOrder = useCallback(async () => {
    if (!restaurant?.id || !lines.length) {
      toast.error('Your cart is empty.');
      return;
    }
    if (!String(address || '').trim()) {
      toast.error('Please enter a delivery address.');
      return;
    }
    if (!isValidCoords(coords)) {
      toast.error('Please choose the delivery location on the map.');
      return;
    }
    setBusy(true);
    try {
      const payload = buildWebOrderPayload({
        restaurantId: restaurant.id,
        branchId: restaurant.branchId ?? null,
        lines,
        orderType: ORDER_TYPE,
        notes: buildOrderNotes({ leaveAtDoor, tipAmount: tip, payment }),
        deliveryAddress: address,
        deliveryCoords: coords,
      });
      const data = await createWebOrder(payload, token);
      const ord = data?.order;
      const label = ord?.order_number != null ? `#${ord.order_number}` : ord?.uuid ? String(ord.uuid).slice(0, 8) : '';
      toast.success(label ? `Order placed ${label}` : 'Order placed');
      actions.clearCart();
      actions.setFee({ delivery: 0, taxRatePct: 0 });
      navigate('/');
    } catch (e) {
      toast.error(e?.message || 'Could not place order');
    } finally {
      setBusy(false);
    }
  }, [restaurant, lines, address, coords, leaveAtDoor, tip, payment, token, actions, navigate]);

  if (!hasItems) {
    return <Navigate to="/" replace />;
  }

  if (!isAuthenticated) {
    return (
      <div className="ch-page">
        <div className="ch-page__inner">
          <div className="ch-auth">
            <div className="ch-auth__card">
              <GuestLoginPanel
                heading="YOU'RE 1 STEP AWAY!"
                showContinueAsGuest
                onGuestContinue={() =>
                  toast('Enter your email — we will send a one-time code. No password needed.', { duration: 3800 })
                }
                onAuthenticated={() => toast.success('Signed in. You can place your order.')}
              />
            </div>
            {summary}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ch-page">
      <div className="ch-page__inner">
        <div className="ch-layout">
          <div>
            <section className="ch-card">
              <h2 className="ch-section-title">ADDRESS</h2>
              <SavedAddressPicker token={token} onSelect={handleSavedAddressSelect} reloadRef={savedAddressPickerRef} />
              <div className="ch-map-wrap">
                <div ref={mapElRef} className="ch-map" aria-label="Delivery location map" />
                {!mapReady ? (
                  <div className="ch-map__fallback">
                    <span>Loading map...</span>
                  </div>
                ) : null}
              </div>
              <div className="ch-location-tools">
                <button type="button" className="ch-location-btn" onClick={requestCurrentLocation} disabled={isResolvingLocation}>
                  Use my current location
                </button>
                <button
                  type="button"
                  className="ch-location-btn ch-location-btn--light"
                  onClick={matchTypedAddressToMap}
                  disabled={isResolvingLocation || !String(address || '').trim()}
                >
                  Match address to map
                </button>
              </div>
              <p className="ch-location-help">{locationStatus}</p>
              {locationError ? (
                <p className="ch-location-error" role="alert">
                  {locationError}
                </p>
              ) : null}
              <div className="ch-address-row">
                <textarea
                  className="ch-address-text"
                  value={address}
                  onChange={(e) => {
                    addressSourceRef.current = 'manual';
                    setAddress(e.target.value);
                    setCoords(null);
                    setLocationStatus('Address changed. Match it to the map before placing the order.');
                  }}
                  onBlur={() => {
                    if (String(address || '').trim()) void matchTypedAddressToMap();
                  }}
                  placeholder="Detecting your delivery address..."
                  rows={3}
                  aria-label="Delivery address"
                />
                <button
                  type="button"
                  className="ch-icon-btn"
                  aria-label="Use current location"
                  title="Use current location"
                  onClick={requestCurrentLocation}
                  disabled={isResolvingLocation}
                >
                  <img src={locationIcon} alt="Location" style={{ width: 20, height: 20 }} />
                </button>
              </div>
              {isValidCoords(coords) ? (
                <div className="ch-coords">
                  Lat {Number(coords.latitude).toFixed(6)} · Long {Number(coords.longitude).toFixed(6)}
                </div>
              ) : null}
              {String(address || '').trim() && (
                <button
                  type="button"
                  className="ch-save-addr-btn"
                  onClick={saveCurrentAddress}
                  disabled={savingAddress}
                >
                  {savingAddress ? 'Saving...' : 'Save this address'}
                </button>
              )}
              <div className="ch-row-toggle">
                <span>Leave at door</span>
                <button
                  type="button"
                  className={`ch-switch ${leaveAtDoor ? 'is-on' : ''}`}
                  onClick={() => setLeaveAtDoor((v) => !v)}
                  aria-pressed={leaveAtDoor}
                  aria-label="Leave at door"
                />
              </div>
            </section>

            <section className="ch-card">
              <h2 className="ch-section-title">DELIVERY OPTIONS</h2>
              <div className="ch-options">
                {Object.entries(DELIVERY_SPEED).map(([key, opt]) => (
                  <label key={key} className={`ch-radio ${speed === key ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="delivery-speed"
                      checked={speed === key}
                      onChange={() => setSpeed(key)}
                    />
                    <div className="ch-radio__main">
                      <div className="ch-radio__title">{opt.label}</div>
                      <div className="ch-radio__sub">{opt.eta}</div>
                    </div>
                    <div className="ch-radio__price">{opt.fee > 0 ? `$${opt.fee.toFixed(2)}` : 'Free'}</div>
                  </label>
                ))}
              </div>
            </section>

            <section className="ch-card">
              <h2 className="ch-section-title">TIP YOUR RIDER</h2>
              <div className="ch-banner">100% of the tips go directly to your rider.</div>
              <div className="ch-tip-row">
                {[0, 5, 10, 15].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    className={`ch-tip-btn ${tip === amt ? 'is-on' : ''}`}
                    onClick={() => setTip(amt)}
                  >
                    {amt === 0 ? 'Not now' : `$${amt.toFixed(2)}`}
                  </button>
                ))}
              </div>
            </section>

            <section className="ch-card">
              <h2 className="ch-section-title">PAYMENT METHOD</h2>
              <div className="ch-options">
                {[
                  { id: 'credit', title: 'Dindin+ Credit' },
                  { id: 'card', title: 'Credit / Debit card' },
                  { id: 'gpay', title: 'Google Pay' },
                  { id: 'apple', title: 'Apple Pay' },
                ].map((m) => (
                  <label key={m.id} className={`ch-radio ${payment === m.id ? 'is-selected' : ''}`}>
                    <input type="radio" name="pay" checked={payment === m.id} onChange={() => setPayment(m.id)} />
                    <div className="ch-radio__main">
                      <div className="ch-radio__title">{m.title}</div>
                    </div>
                  </label>
                ))}
              </div>
              {payment === 'card' ? (
                <div className="ch-pay-fields">
                  <input placeholder="Card number" autoComplete="off" />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input placeholder="MM / YY" autoComplete="off" />
                    <input placeholder="CVV" autoComplete="off" />
                  </div>
                </div>
              ) : null}
              <button type="button" className="ch-place" onClick={placeOrder} disabled={busy}>
                {busy ? 'Placing order…' : 'Place order'}
              </button>
            </section>
          </div>

          {summary}
        </div>
      </div>
    </div>
  );
}
